<template>
  <div>
    <div :class="$style.radiobtnContainer">
      <b-field grouped class="margin-top6 columns">
        <!-- Select Project Type Options -->
        <b-radio-button
          expanded
          v-for="option in RecurrenceTypeOptions"
          v-model="selectedRecurrenceType"
          :key="option"
          :native-value="option"
          type="is-primary"
          class="column is-3 padding-right5 radio-option-style-first"
          :class="
            selectedRecurrenceType === option
              ? $style.activeRadioBtn
              : $style.radioBtn
          "
        >
          <span
            :class="
              selectedRecurrenceType === option
                ? $style.activeHeader
                : $style.header
            "
          >
            <div>
              <p class="text-size-body bold">
                <b>{{ recurrenceTypeDisplayInfo(option).title }}</b>
              </p>
              <p class="text-size weight-initial margin-top8">
                {{ recurrenceTypeDisplayInfo(option).infotext1 }}
              </p>
            </div>
          </span>
        </b-radio-button>
      </b-field>
    </div>

    <!-- Date Picker -->
    <div class="columns is-multiline">
      <!-- Select Start Date -->
      <div class="column is-half margin-bottom6">
        <div class="field" :class="$style.period">
          <label class="label">
            Start Date
            <!-- Add 'Optional' label only when the recurrenceType is not 'MULTIPLE' -->
            <span
              v-if="
                dirtyRecurrenceSetting.recurrenceType !==
                RecurrenceType.MULTIPLE
              "
            >
              (Optional)
            </span>
          </label>
          <div class="columns">
            <div class="column">
              <b-datepicker
                placeholder="Select a start date"
                icon="calendar-today"
                icon-pack="mdi"
                v-model="scheduledRecurrenceStartDate"
                @input="updateTotalDataCollectionPeriod"
                :class="[$style.customCalendarIcon, 'calender-icon']"
                :date-formatter="(date) => formatDateRange(date)"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Select Sampling Type -->
      <div class="column is-half padding-left4">
        <b-field :class="$style.SamplingTypeDropdown" label="Sampling Type">
          <b-dropdown
            v-model="selectedSamplingType"
            aria-role="list"
            :class="[$style.customSamplingDropdown, 'simple-dropdown']"
          >
            <button
              class="button simple-dropdown-button"
              slot="trigger"
              slot-scope="{ active }"
            >
              <!-- Audit SamplingType Selected Option -->
              <span>
                {{ auditSamplingTypeDisplayOptions[selectedSamplingType] }}
              </span>
              <b-icon :icon="active ? 'menu-up' : 'menu-down'"></b-icon>
            </button>
            <!-- Audit SamplingType Options -->
            <b-dropdown-item
              :value="value"
              v-for="value in AuditSamplingType"
              :key="value"
              aria-role="listitem"
              class="simple-dropdown-item"
            >
              {{ auditSamplingTypeDisplayOptions[value] }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>

      <!-- Select Data Collection Period Details for One-Off or Ongoing RecurrenceType -->
      <div
        v-if="dirtyRecurrenceSetting.recurrenceType === RecurrenceType.ONE_OFF"
        class="column is-full"
      >
        <label :class="$style.cycleLabel">
          {{ dirtyRecurrenceSetting.dataCollectionPeriodSettings[0].auditName }}
        </label>
        <AuditCycleDataSetup
          :key="`audit_cycle_${dataCollectionPeriodKey}_${0}`"
          :isCreateProject="false"
          :projectCriteria="projectCriteria"
          :projectSites="projectSites"
          :projectDataCollectors="projectDataCollectors"
          :selectedCriteria="
            dirtyRecurrenceSetting.dataCollectionPeriodSettings[0]
              .auditCriteriaIds || []
          "
          :selectedSites="
            dirtyRecurrenceSetting.dataCollectionPeriodSettings[0]
              .auditSiteIds || []
          "
          :selectedDataCollectors="
            dirtyRecurrenceSetting.dataCollectionPeriodSettings[0].auditParticipants.map(
              (ap) => ap.email,
            ) || []
          "
          :recurrence="dirtyRecurrenceSetting.recurrenceType"
          @update="(data) => handleUpdatedCycleData(data, 0)"
        />
      </div>
    </div>

    <!-- Option to Schedule Data Collection Period(s) for RecurrenceType MULTIPLE  -->
    <div
      v-if="dirtyRecurrenceSetting.recurrenceType === RecurrenceType.MULTIPLE"
    >
      <hr />
      <br />
      <div class="columns is-multiline">
        <!-- Select duration of recurrence cycles(Data Collection Period/Audit) -->
        <div class="column is-2">
          <b-field label="Every">
            <b-numberinput
              placeholder="Number"
              v-model="interval"
              @input="updateTotalDataCollectionPeriod"
              :controls="false"
            >
            </b-numberinput>
          </b-field>
          <!-- Error msg for validating input duration of recurrence cycles(Data Collection Period/Audit) -->
          <p v-if="showIntervalError" :class="$style.validationErrorMessage">
            Value cannot be empty or less than 1
          </p>
        </div>

        <!-- Select Recurrence Cycle Frequency -->
        <div class="column is-3 no-label-cycle padding-left-none">
          <b-field label="Cycle">
            <b-dropdown
              v-model="multipleRecurrenceType"
              @input="updateTotalDataCollectionPeriod"
              aria-role="list"
              class="participant-rowField-container"
            >
              <button
                class="button is-primary role-select-button"
                slot="trigger"
              >
                <span class="text-input">
                  {{ multipleRecurrenceType }}
                </span>
                <b-icon type="is-black" icon="menu-down" size="is-small" />
              </button>

              <b-dropdown-item
                :value="value.id"
                v-for="value in multipleRecurrenceFrequencyOptions"
                :key="value.id"
                aria-role="listitem"
                class="multiple-recurrence-input text-size-body"
              >
                {{ value.name }}
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
        </div>

        <!-- Select no of Recurrence Cycles(Data Collection Period/Audit) -->
        <div class="column is-7 no-label-cycle">
          <label class="label">Produce a total of</label>
          <div class="inline-input-audit-container">
            <b-field :class="$style.auditNumberField">
              <b-numberinput
                v-model="auditNumber"
                @input="updateTotalDataCollectionPeriod"
                placeholder="Number"
                :controls="false"
                :class="
                  showTotalError
                    ? $style.auditNumberErrorStyle
                    : $style.auditNumberStyle
                "
              ></b-numberinput>
            </b-field>
            <span class="inline-input-audit-text"
              >Data Collection Period(s)</span
            >
          </div>
          <!-- Error msg for validating input number of Recurrence Cycles(Data Collection Period/Audit) -->
          <p v-if="showTotalError" :class="$style.validationErrorMessage">
            Value must be between 1 to 10
          </p>
        </div>
      </div>
      <div v-if="interval >= 1">
        <!-- Select details for each Cycle(Data Collection Period/Audit)  -->
        <div
          v-for="(
            dataCollectionPeriod, datepickerIndex
          ) in dataCollectionPeriods"
          :key="datepickerIndex"
          :value="dataCollectionPeriod"
          class="columns is-multiline"
        >
          <!-- Option to select Date Range for Cycle(Data Collection Period/Audit) -->
          <ProjectCreationMultipleDatePicker
            :key="dataCollectionPeriodKey"
            :datepickerItem="dataCollectionPeriod"
            :periodIndex="datepickerIndex"
            @updateDatePicker="updateDataCollectionPeriodInitialSettings"
          />

          <!-- 
          Option to select Cycle(Data Collection Period/Audit) setup data such as:
           1. Criteria
           2. Sites
           3. Data Collectors
          -->
          <AuditCycleDataSetup
            :key="`audit_cycle_${dataCollectionPeriodKey}_${datepickerIndex}`"
            :isCreateProject="false"
            :projectCriteria="projectCriteria"
            :projectSites="projectSites"
            :projectDataCollectors="projectDataCollectors"
            :selectedCriteria="dataCollectionPeriod.auditCriteriaIds || []"
            :selectedSites="dataCollectionPeriod.auditSiteIds || []"
            :selectedDataCollectors="
              dataCollectionPeriod.auditParticipants.map((ap) => ap.email) || []
            "
            :recurrence="dirtyRecurrenceSetting.recurrenceType"
            @update="(data) => handleUpdatedCycleData(data, datepickerIndex)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import {
  MultipleRecurrenceFrequencyOptions,
  RecurrenceSettingPayload,
  DataCollectionPeriodDTO,
  ProjectDetailPayload,
  DataCollectionPeriodSettingsPayload,
} from '@/store/modules/projects/types/projects.types';
import { StringInputOption } from '@/jbi-shared/types/form.types';
import ProjectCreationMultipleDatePicker from './ProjectCreationMultipleDatepicker.vue';
import moment from 'moment';
import { getStartDateTime, getEndDateTime } from '../../../utils/date.util';
import AuditCycleDataSetup from './AuditCycleDataSetup.vue';
import {
  AuditSamplingType,
  MultipleRecurrenceType,
  RecurrenceType,
} from '@/jbi-shared/types/project.types';
import { cloneData } from '@/utils/general.util';
import { AuditSamplingTypeDisplayOptions } from '@/store/modules/audits/types/audits.types';
const dirtyMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {
    ProjectCreationMultipleDatePicker,
    AuditCycleDataSetup,
  },
})
export default class NewAuditSettings extends Vue {
  @Prop() public projectDetail!: ProjectDetailPayload;
  @Prop() public latestDate!: Date;
  @Prop({ type: Array, default: () => [] })
  public projectCriteria!: Array<{
    title: string;
    uniqueCriteriaId: string | number;
  }>;
  @Prop({ type: Array, default: () => [] })
  public projectSites!: Array<{ id: number; name: string }>;
  @Prop({ type: Array, default: () => [] })
  public projectDataCollectors!: Array<{
    email: string;
    siteIds: number[];
  }>;
  @Prop() public auditCount!: number;
  @Prop() public prevAuditCriteriaIds!: number[];

  public projectExpiryDatePicker: any = null;
  public defaultEndDate: any = moment(this.latestDate).add(1, 'M');
  public initialDataCollectionPeriodData: DataCollectionPeriodDTO = {
    auditNumber: 1,
    auditName: this.getAuditName(0),
    startDate: getStartDateTime(this.latestDate),
    endDate: getEndDateTime(this.defaultEndDate.subtract(1, 'd')),
    // Props may not be ready yet during class-field init (vue-class-component)
    auditCriteriaIds: this.prevAuditCriteriaIds || [],
    auditSiteIds: (this.projectSites || []).map((ps) => ps.id),
    auditParticipants: cloneData(this.projectDataCollectors || []),
  };
  public dirtyRecurrenceSetting: RecurrenceSettingPayload = {
    recurrenceType: RecurrenceType.ONE_OFF,
    multipleRecurrenceType: MultipleRecurrenceType.DAILY,
    auditSamplingType: AuditSamplingType.TARGET_SAMPLE_SIZE,
    interval: 1,
    auditNumber: 1,
    scheduledRecurrenceStartDate: getStartDateTime(this.latestDate),
    scheduledRecurrenceEndDate: null,
    enableGrip: true,
    dataCollectionPeriodSettings: [
      {
        ...this.initialDataCollectionPeriodData,
      },
    ],
  };
  public dataCollectionPeriodKey: number = Math.floor(Math.random() * 999);
  public showTotalError: boolean = false;
  public showIntervalError: boolean = false;

  public getAuditName(index: number): string {
    return this.auditCount !== 0
      ? 'Follow-Up ' + Number(this.auditCount + index)
      : 'Baseline';
  }

  public getAuditCycleDataSetupKey(index: number) {
    let key = Math.floor(Math.random() * 999);
    return `audit_cycle_${key++}_${index}`;
  }

  get RecurrenceTypeOptions(): RecurrenceType[] {
    return [RecurrenceType.ONE_OFF, RecurrenceType.MULTIPLE];
  }

  get RecurrenceType(): typeof RecurrenceType {
    return RecurrenceType;
  }

  get AuditSamplingType(): typeof AuditSamplingType {
    return AuditSamplingType;
  }

  get auditSamplingTypeDisplayOptions() {
    return AuditSamplingTypeDisplayOptions;
  }

  get selectedSamplingType(): AuditSamplingType {
    return this.dirtyRecurrenceSetting.auditSamplingType;
  }

  set selectedSamplingType(newValue) {
    this.dirtyRecurrenceSetting.auditSamplingType = newValue;
    this.dataCollectionPeriodKey += 1;
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
  }

  get selectedRecurrenceType(): RecurrenceType {
    return this.dirtyRecurrenceSetting.recurrenceType;
  }

  set selectedRecurrenceType(newValue) {
    this.dirtyRecurrenceSetting.recurrenceType = newValue;
    this.dirtyRecurrenceSetting.auditNumber = 1;
    this.dirtyRecurrenceSetting.interval = 1;
    this.dirtyRecurrenceSetting.multipleRecurrenceType =
      newValue === RecurrenceType.MULTIPLE
        ? MultipleRecurrenceType.MONTHLY
        : null;

    if (newValue === RecurrenceType.MULTIPLE) {
      this.updateTotalDataCollectionPeriod();
    }
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
  }

  get scheduledRecurrenceStartDate() {
    const recurrenceStartDate: any = getStartDateTime(
      this.dirtyRecurrenceSetting.scheduledRecurrenceStartDate,
    );

    return typeof recurrenceStartDate === 'string'
      ? getStartDateTime(recurrenceStartDate)
      : recurrenceStartDate;
  }

  set scheduledRecurrenceStartDate(newValue) {
    this.dirtyRecurrenceSetting.scheduledRecurrenceStartDate = getStartDateTime(
      newValue,
    );
    this.dataCollectionPeriodKey += 1;
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
  }

  get dataCollectionPeriods() {
    return this.dirtyRecurrenceSetting.dataCollectionPeriodSettings
      ? this.dirtyRecurrenceSetting.dataCollectionPeriodSettings
      : this.updateTotalDataCollectionPeriod();
  }

  get multipleRecurrenceFrequencyOptions(): StringInputOption[] {
    return Object.entries(MultipleRecurrenceFrequencyOptions).map(
      ([key, value]) => {
        return {
          id: key,
          name: value,
        };
      },
    );
  }

  get multipleRecurrenceType() {
    return this.dirtyRecurrenceSetting.multipleRecurrenceType !== undefined &&
      this.dirtyRecurrenceSetting.multipleRecurrenceType
      ? this.dirtyRecurrenceSetting.multipleRecurrenceType
      : MultipleRecurrenceType.MONTHLY;
  }

  set multipleRecurrenceType(newValue) {
    this.dirtyRecurrenceSetting.multipleRecurrenceType = newValue;
    this.dataCollectionPeriodKey += 1;
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
  }

  get interval(): number {
    return this.dirtyRecurrenceSetting.interval
      ? this.dirtyRecurrenceSetting.interval
      : 0;
  }

  set interval(newValue: number) {
    this.dirtyRecurrenceSetting.interval = newValue;
    this.showIntervalError = !newValue || newValue < 1 ? true : false;
    this.dataCollectionPeriodKey += 1;
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
  }

  get auditNumber(): number {
    return this.dirtyRecurrenceSetting.auditNumber
      ? this.dirtyRecurrenceSetting.auditNumber
      : 0;
  }

  set auditNumber(newValue: number) {
    this.dirtyRecurrenceSetting.auditNumber = newValue;
    this.showTotalError = newValue === 0 ? true : false;
    this.dataCollectionPeriodKey += 1;
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
  }

  /** Returns title and description for each recurrence type, used for display purposes  */
  public recurrenceTypeDisplayInfo(
    type: RecurrenceType,
  ): {
    title: string;
    infotext1: string;
  } {
    switch (type) {
      case RecurrenceType.ONE_OFF:
        return {
          title: 'One-off',
          infotext1: 'Add 1 new data collection period to the project.',
        };
      case RecurrenceType.MULTIPLE:
        return {
          title: 'Multiple',
          infotext1:
            'Add multiple new data collection period to the project that will start and stop at a scheduled time.',
        };
      default:
        return {
          title: '',
          infotext1: '',
        };
    }
  }

  /**
   * Handles assigning auditCriteriaIds, auditSiteIds, and auditParticipants to the selected index,
   * then emits the updated data to the parent component to reflect the changes
   */
  public handleUpdatedCycleData(
    data: {
      auditCriteriaIds: Array<string | number>;
      auditSiteIds: number[];
      auditParticipants: Array<{ email: string; siteIds: number[] }>;
    },
    index: number,
  ) {
    // Store the selected data in the recurrence settings
    this.dirtyRecurrenceSetting.dataCollectionPeriodSettings[
      index
    ].auditCriteriaIds = data.auditCriteriaIds;
    this.dirtyRecurrenceSetting.dataCollectionPeriodSettings[
      index
    ].auditSiteIds = data.auditSiteIds;
    this.dirtyRecurrenceSetting.dataCollectionPeriodSettings[
      index
    ].auditParticipants = data.auditParticipants;

    // Emit update to parent
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);

    // Also emit the criteria and sites separately
    this.$emit('update-selections', {
      criteria: data.auditCriteriaIds,
      sites: data.auditSiteIds,
    });
  }

  public getDate(newValue: Date): string {
    return (
      newValue.getDate() +
      ' ' +
      dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  public formatDateRange(date: Date): string {
    return this.getDate(date);
  }

  public updateDataCollectionPeriodInitialSettings(newValue: {
    auditName: string;
    auditNumber: number;
    startDate: Date;
    endDate: Date;
  }) {
    const tempUpdatedSettings = {
      ...this.dirtyRecurrenceSetting,
    };

    tempUpdatedSettings.dataCollectionPeriodSettings[newValue.auditNumber] = {
      ...tempUpdatedSettings.dataCollectionPeriodSettings[newValue.auditNumber],
      ...newValue,
    };

    this.$emit('updateAuditSetting', tempUpdatedSettings);
  }

  /**
   * Updates initial settings for the selectedRecurrenceType 'MULTIPLE'.
   *
   * When the user selects the number of recurrence cycles,
   * this function generates and initializes the default data structure for each cycle.
   */

  public updateTotalDataCollectionPeriod() {
    const dataCollectionPeriodSettings = [];
    const scheduledStartDate = moment(this.scheduledRecurrenceStartDate);
    if (this.selectedRecurrenceType !== RecurrenceType.MULTIPLE) {
      dataCollectionPeriodSettings.push({
        startDate: getStartDateTime(scheduledStartDate),
        endDate: null,
        auditNumber: 1,
        auditName: this.getAuditName(0),
        auditCriteriaIds:
          (this.prevAuditCriteriaIds || []).length > 0
            ? this.prevAuditCriteriaIds
            : (this.projectCriteria || []).map((pc) => pc.uniqueCriteriaId), // Default prev audit cycle criteria should be selected
        auditSiteIds: (this.projectSites || []).map((ps) => ps.id),
        auditParticipants: cloneData(this.projectDataCollectors || []),
      });
    } else {
      let auditNumberValue = this.auditNumber;
      /** Reset auditNumberValue to 1 if it exceeds 10, and exit early to prevent further processing */
      if (auditNumberValue > 10) {
        auditNumberValue = 1;
        return;
      }

      for (let value = 0; value < auditNumberValue; value++) {
        const startDate =
          value > 0
            ? scheduledStartDate.add(1, 'd').toDate()
            : scheduledStartDate.toDate();
        const newAuditName = this.getAuditName(value);
        switch (this.multipleRecurrenceType) {
          case Object.keys(MultipleRecurrenceFrequencyOptions)[0]:
            scheduledStartDate.add(this.interval, 'd');
            break;
          case Object.keys(MultipleRecurrenceFrequencyOptions)[1]:
            scheduledStartDate.add(this.interval, 'w');
            break;
          case Object.keys(MultipleRecurrenceFrequencyOptions)[2]:
            scheduledStartDate.add(this.interval, 'M');
            break;
          default:
            scheduledStartDate.add(this.interval, 'y');
            break;
        }
        const newEndDate: Date = scheduledStartDate.subtract(1, 'd').toDate();
        const dateRangePeriod: Date[] = [startDate, newEndDate];

        dataCollectionPeriodSettings.push({
          startDate: getStartDateTime(dateRangePeriod[0]),
          endDate: getEndDateTime(dateRangePeriod[1]),
          auditNumber: value + 1,
          auditName: newAuditName,
          auditCriteriaIds:
            (this.prevAuditCriteriaIds || []).length > 0
              ? this.prevAuditCriteriaIds
              : (this.projectCriteria || []).map((pc) => pc.uniqueCriteriaId), // Default prev audit cycle criteria should be selected
          auditSiteIds: (this.projectSites || []).map((ps) => ps.id),
          auditParticipants: cloneData(this.projectDataCollectors || []),
        });
      }
    }

    const multipleAuditCyclesData: DataCollectionPeriodSettingsPayload = {
      ...dataCollectionPeriodSettings,
    };
    this.dirtyRecurrenceSetting.dataCollectionPeriodSettings = multipleAuditCyclesData;
    this.$emit('updateAuditSetting', this.dirtyRecurrenceSetting);
    return multipleAuditCyclesData;
  }
}
</script>
<style lang="scss" module>
.radioBtn label {
  background-color: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  height: 147px;
  color: #444f51;
  font-size: 16px;
  font-weight: 400;
  margin-right: unset !important;
}
.auditNumberField {
  width: 40%;
}
.auditNumberStyle {
  input {
    border: 0px solid;
    background-color: transparent;

    :focus {
      color: transparent;
    }
  }
}
.auditNumberErrorStyle {
  input {
    border: 1px solid #f14668;
  }
}
.activeRadioBtn label {
  color: #fff !important;
  font-size: 16px;
  font-weight: 400;
  background-color: #21b5ea !important;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  height: 147px;
}
.header,
.text {
  display: block;
  height: 100%;
  max-width: 100%;
  text-overflow: clip;
  white-space: normal;
  text-align: start;
  font-size: 14px;
  line-height: 17px;
  color: #444f51;
  padding: 0.7rem 0.75rem 1.5rem 0.75rem;
  flex-basis: 100%;
}
.activeHeader {
  color: #fff;
  display: block;
  height: 100%;
  max-width: 100%;
  text-overflow: clip;
  white-space: normal;
  text-align: start;
  font-size: 14px;
  line-height: 17px;
  padding: 0.7rem 0.75rem 1.5rem 0.75rem;
  flex-basis: 100%;
}
.radiobtnContainer {
  margin-bottom: 10px;
  padding-right: 150px;
}
.extraData {
  color: #9a9a9a;
  font-size: 12px;
  line-height: 14px;
  font-weight: 400;
  text-align: left;
}
.validationErrorMessage {
  color: red;
  font-size: 12px;
  margin-top: 4px;
}
.SamplingTypeDropdown {
  display: inline-block;
  padding-left: 50px;
  width: 100%;
}
.customSamplingDropdown {
  // width: 250px;
}
.customCalendarIcon {
  i::before {
    padding-top: 13px;
    padding-bottom: 12px;
    width: 15px;
    margin-right: 10px;
  }
}
.period {
  label {
    text-align: left;
  }
  width: 350px;
}

.cycleLabel {
  text-align: left;
  font-weight: 600;
  font-size: 16px;
  color: #444f51;
  padding-bottom: 1rem;
}
</style>
